import { ref, onBeforeUnmount, Ref, computed, watch } from "vue";
import {
  DocumentRevisionCheckDocument,
  type DocumentRevisionCheckQuery,
} from "@/graphql/operations";
import graphqlClient from "@/graphql/client";

export type RevisionProcessingState = "processing" | "succeeded" | "failed";

export function getRevisionProcessingState(
  document: DocumentRevisionCheckQuery["document"],
): RevisionProcessingState {
  if (!document || document.mimeType !== "application/pdf") {
    return "succeeded";
  }

  if (document.isProcessing) {
    return "processing";
  }

  const latestRevision = document.documentRevisions.nodes[0];
  const failedCandidateIsLatest =
    latestRevision?.hasProcessingError === true &&
    latestRevision.revisionNumber > (document.currentRevisionNumber ?? 0);

  return failedCandidateIsLatest ? "failed" : "succeeded";
}

export function useRevisionChecker(
  docId: Ref<string | undefined>,
  onReady?: () => void,
) {
  const isReady = computed(() => !!docId.value);

  const isProcessing = ref(false);
  const hasProcessingError = ref(false);
  const processingState = computed<RevisionProcessingState>(() => {
    if (isProcessing.value) return "processing";
    return hasProcessingError.value ? "failed" : "succeeded";
  });

  let intervalId: number | null = null;
  let attemptGeneration = 0;
  let activeCheck: {
    generation: number;
    promise: Promise<void>;
  } | null = null;

  function startPolling(checkImmediately = true) {
    if (intervalId == null) {
      intervalId = window.setInterval(checkRevision, 6000);
    }
    if (checkImmediately) {
      void checkRevision();
    }
  }

  function stopPolling() {
    if (intervalId != null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  }

  async function fetchRevisionData(documentId: string) {
    return graphqlClient
      .query(
        DocumentRevisionCheckDocument,
        { id: documentId },
        { requestPolicy: "network-only" },
      )
      .toPromise();
  }

  async function runRevisionCheck(generation: number, documentId: string) {
    const result = await fetchRevisionData(documentId);
    if (
      generation !== attemptGeneration ||
      !isReady.value ||
      result.data == null
    ) {
      return;
    }
    const previousState = processingState.value;
    let currentState = getRevisionProcessingState(result.data.document);

    if (previousState === "processing" && currentState === "succeeded") {
      const finalResult = await fetchRevisionData(documentId);
      if (
        generation !== attemptGeneration ||
        !isReady.value ||
        finalResult.data == null
      ) {
        return;
      }
      currentState = getRevisionProcessingState(finalResult.data.document);
      if (currentState === "succeeded") {
        onReady?.();
      }
    }

    isProcessing.value = currentState === "processing";
    hasProcessingError.value = currentState === "failed";

    if (isProcessing.value) {
      startPolling();
    } else {
      stopPolling();
    }
  }

  function checkRevision(): Promise<void> {
    if (!isReady.value) return Promise.resolve();

    const generation = attemptGeneration;
    const documentId = docId.value!;
    if (activeCheck?.generation === generation) {
      return activeCheck.promise;
    }

    const promise = runRevisionCheck(generation, documentId).finally(() => {
      if (activeCheck?.promise === promise) {
        activeCheck = null;
      }
    });
    activeCheck = { generation, promise };
    return promise;
  }

  watch(
    docId,
    (newDocumentId) => {
      attemptGeneration += 1;
      stopPolling();
      isProcessing.value = false;
      hasProcessingError.value = false;
      if (newDocumentId) {
        startPolling();
      }
    },
    { immediate: true },
  );

  function stop() {
    attemptGeneration += 1;
    stopPolling();
  }

  onBeforeUnmount(stop);

  function beginProcessing(mimeType?: string) {
    attemptGeneration += 1;
    hasProcessingError.value = false;
    isProcessing.value = true;
    startPolling(mimeType !== "application/pdf");
  }

  return {
    isProcessing,
    hasProcessingError,
    processingState,
    beginProcessing,
    stop,
  };
}
